import { Box, BoxProps, Flex, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import RouteLink from '@/components/RouteLink';
import { RegistryPackage, RegistryXrd } from '@/graphql/upbound-graphql';
import { isRegistryXRD } from '@/utils/guards/models';
import useParams from '@/utils/hooks/useParams';

import PackageTabContent from './PackageTabContent';

const [firstColumnWidth, secondColumnWidth, thirdColumnWidth] = ['415px', '415px', '160px'];

const headers = [
  {
    key: 'xrds-kind-header',
    children: 'Kind',
    width: firstColumnWidth,
  },
  {
    key: 'xrds-group-header',
    children: 'Group',
    width: secondColumnWidth,
  },
  {
    key: 'xrds-version-header',
    children: 'Version',
    width: thirdColumnWidth,
  },
];

const TabContentXrds: React.FC<
  BoxProps & {
    data: RegistryPackage;
  }
> = ({ data, ...props }) => {
  const router = useRouter();
  const params = useParams(router);
  const query = decodeURIComponent(params.get('query') || '');

  const xrds = data.resources.reduce<Array<RegistryXrd>>((result, resource) => {
    if (!isRegistryXRD(resource)) {
      return result;
    }

    if (!query) {
      return [...result, resource];
    }

    const sanitizedValue = query.toLowerCase().trim();

    if (
      !resource.group.toLowerCase().includes(sanitizedValue) &&
      !resource.kind.toLowerCase().includes(sanitizedValue) &&
      !resource.referenceableVersion.toLowerCase().includes(sanitizedValue)
    ) {
      return result;
    }

    return [...result, resource];
  }, []);

  const prepend = `/${data.packageType.toLowerCase()}s/${data.account}/${data.name}/${data.version}`;

  const rows = useMemo(() => {
    if (xrds.length < 1) {
      return (
        <Box m="30px 0">
          <Text textAlign="center" fontStyle="oblique">
            No XRD&apos;s found.
          </Text>
        </Box>
      );
    }

    return xrds.map(row => (
      <Box
        key={`xrd-row-${row.group}/${row.kind}/${row.referenceableVersion}`}
        p={{ base: '30px 0', xl: '13px 0 15px 0' }}
        borderBottom="1px solid"
        borderBottomColor="white.4"
        width="100%"
      >
        <Flex display={{ base: 'flex', xl: 'none' }} flexDirection="column" alignItems="flex-start">
          <RouteLink
            prepend={prepend}
            to="resourceDetails"
            params={{
              resourceGroup: row.group,
              resourceKind: row.kind,
              resourceVersion: row.referenceableVersion,
            }}
            fontSize="14px"
            fontWeight={900}
            textDecoration="underline"
            _hover={{ textDecoration: 'none' }}
            color="grey.0"
            mb="10px"
            noOfLines={1}
          >
            {row.kind}
          </RouteLink>
          <Text as="span" fontSize="14px" color="grey.0" mb="10px" noOfLines={1}>
            {row.group}
          </Text>
          <Text as="span" fontSize="14px" color="grey.0">
            {row.referenceableVersion}
          </Text>
        </Flex>
        <Flex display={{ base: 'none', xl: 'flex' }}>
          <Box width={firstColumnWidth} p="15px" mr="auto">
            <RouteLink
              prepend={prepend}
              to="resourceDetails"
              params={{
                resourceGroup: row.group,
                resourceKind: row.kind,
                resourceVersion: row.referenceableVersion,
              }}
              fontSize="14px"
              fontWeight={900}
              textDecoration="underline"
              _hover={{ textDecoration: 'none' }}
              color="grey.0"
              noOfLines={1}
            >
              {row.kind}
            </RouteLink>
          </Box>
          <Box width={secondColumnWidth} p="15px" mr="auto">
            <Text as="span" fontSize="14px" color="grey.0" noOfLines={1}>
              {row.group}
            </Text>
          </Box>
          <Box width={thirdColumnWidth} p="15px">
            <Text as="span" fontSize="14px" color="grey.0">
              {row.referenceableVersion}
            </Text>
          </Box>
        </Flex>
      </Box>
    ));
  }, [xrds, prepend]);

  return (
    <PackageTabContent
      {...props}
      mt="35px"
      description="Composite Resource Definitions (XRDs) define new API types that are implemented by one or more Compositions."
      placeholder={`Search for XRDs (e.g. ${data.resources
        .slice(0, 2)
        .map(r => r.kind)
        .join(', ')})`}
      headers={headers}
      rows={rows}
      prepend={`${prepend}/xrds`}
    />
  );
};

export default TabContentXrds;
